import Cookies from 'js-cookie'
import { generateID } from './utils'

/**
 * Base class for managing values in both SessionStorage and Cookies
 */
class StorageValue {
  constructor(key, expires = 0) {
    this.key = key
    this.expires = expires
  }

  get(delimiter = '') {
    // Try to get value from Cookie first, then SessionStorage
    let value = Cookies.get(this.key) || sessionStorage.getItem(this.key)
    if (value && delimiter) value = value.split(delimiter)
    return value
  }

  set(value, expires = this.expires) {
    if (!value) {
      Cookies.remove(this.key)
      sessionStorage.removeItem(this.key)
    } else {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      Cookies.set(this.key, stringValue, { expires })
      sessionStorage.setItem(this.key, stringValue)
    }
    return value
  }
}

/**
 * Class for managing ID values
 */
class StorageID extends StorageValue {
  constructor(key, expires = 0) {
    super(key, expires)
    this.get()
  }

  get() {
    let id = super.get()
    if (!id) {
      id = super.set(this.generate())
    }
    return id
  }

  generate() {
    return generateID()
  }
}

/**
 * Main Session class for managing various IDs and values
 */
class Session {
  static KEY_DEFINITIONS = {
    pageLoadId: { id: 'ssPageLoadId', expires: 0, generateIfEmpty: true },
    shopper: { id: 'ssShopperId', expires: 365 },
    sessionId: { id: 'ssSessionIdNamespace', expires: 0, generateIfEmpty: true },
    userId: { id: 'ssUserId', expires: 365, generateIfEmpty: true },
    isuid: { id: '_isuid', expires: 365 },
    viewedProducts: { id: 'ssViewedProducts', expires: 365 * 7, delimiter: ',' },
    columnPreferences: { id: 'ssColumnPreferences', expires: 0 },
    omneoAuthToken: { id: 'omneoAuthToken', expires: 0 },
    omneoAuthExpiry: { id: 'omneoAuthExpiry', expires: 0 },
    walletPass: { id: 'walletPassUrl', expires: 0 },
    pwpMute: { id: 'pwpMute', expires: 0 }
  }

  constructor() {
    this.initializeStorageItems()
    this.setUserID()
    this.setShopperID()
  }

  initializeStorageItems() {
    for (const [key, value] of Object.entries(Session.KEY_DEFINITIONS)) {
      const StorageClass = value.generateIfEmpty ? StorageID : StorageValue
      this[key] = new StorageClass(value.id, value.expires)
      if (value.generateIfEmpty) {
        this[key].get() // This will trigger ID generation if it doesn't exist
      }
    }
  }

  setUserID() {
    const userId = this.userId.get()
    this.isuid.set(userId) // Set _isuid to match ssUserId
  }

  setShopperID() {
    const customerID = window.GM_STATE?.customer?.id
    if (customerID) {
      this.shopper.set(customerID)
    } else {
      // delete shopper if customer is not logged in
      this.shopper.set()
    }
  }
}

export const session = new Session()
